import React, { Fragment } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  ThemeProvider,
  TextField,
  Box,
  Button,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutlined,
  EditOutlined,
  Check,
  Clear,
} from "@mui/icons-material";
import {
  format,
  addDays,
  differenceInDays,
  isAfter,
  isBefore,
  subYears,
} from "date-fns";
import { tableTheme } from "mui-theme";

interface MatrixRow {
  rateBand: number;
  mon: number;
  tue: number;
  wed: number;
  thu: number;
  fri: number;
  sat: number;
  sun: number;
  isEditing?: boolean;
}

interface Matrix {
  start: string;
  end: string;
  rates: MatrixRow[];
}

interface Band {
  formula: string;
}

interface HotelMatrix {
  start: string;
  end: string;
  bands: Band[];
}

interface HotelProps {
  suggestionParams: {
    matrixes: HotelMatrix[];
  };
}

interface State {
  data: MatrixRow[];
  hotel: HotelProps;
  displaySplit: boolean;
  displayNew: boolean;
  splitDate: Date;
  newStartDate: Date;
  newEndDate: Date | null;
  matrixes: Matrix[];
  currentIndex: number;
  minStartNewDate: Date;
}

interface ExportBand {
  formula: string;
}

interface ExportMatrix {
  start: string;
  end: string;
  bands: ExportBand[];
}

export default class RateMatrix extends React.Component<
  { hotel: HotelProps },
  State
> {
  state: State = {
    matrixes: [],
    data: [],
    splitDate: new Date(),
    newStartDate: new Date(),
    newEndDate: null,
    hotel: {} as HotelProps,
    displaySplit: false,
    displayNew: false,
    currentIndex: 0,
    minStartNewDate: new Date(),
  };

  constructor(props: { hotel: HotelProps }) {
    super(props);
    this.state.hotel = props.hotel;
    this.state.matrixes = this.formatData();
    this.state.currentIndex = this.preSelectIndex();
    this.state.data = this.state.matrixes[0].rates;
    this.state.splitDate = this.computeDefaultSplitdate(0);
    this.state.minStartNewDate = addDays(
      new Date(this.state.matrixes[this.state.matrixes.length - 1].end),
      1,
    );
    this.state.newStartDate = this.state.minStartNewDate;
  }

  computeDefaultSplitdate(index: number) {
    const start = new Date(this.state.matrixes[index].start);
    const end = new Date(this.state.matrixes[index].end);
    const diff = Math.floor(differenceInDays(end, start) / 2);
    return addDays(start, diff);
  }

  exportRateMatrixes(): ExportMatrix[] {
    const res: ExportMatrix[] = [];
    let i = 0;
    for (const matrix of this.state.matrixes) {
      res[i] = {
        start: matrix.start,
        end: matrix.end,
        bands: [],
      };
      for (const item of matrix.rates) {
        res[i].bands[item.rateBand - 1] = {
          formula: `[${item.sun},${item.mon},${item.tue},${item.wed},${item.thu},${item.fri},${item.sat}][dow]`,
        };
      }
      i++;
    }
    return res;
  }

  setSplitDate = (value: string) => {
    this.setState({ splitDate: new Date(value) });
  };

  setEndDate = (value: string) => {
    this.setState({ newEndDate: new Date(value) });
  };

  createNewPeriod = () => {
    if (!this.state.newEndDate) return;

    const ms = [...this.state.matrixes];
    const n = this.getNewPeriod(this.state.newStartDate, this.state.newEndDate);
    const newstartDate = addDays(this.state.newEndDate, 1);
    ms.push(n);

    this.setState({
      currentIndex: ms.length - 1,
      matrixes: ms,
      displaySplit: false,
      data: n.rates,
      minStartNewDate: newstartDate,
      newStartDate: newstartDate,
      newEndDate: null,
      displayNew: false,
    });
  };

  getNewPeriod(start: Date, end: Date): Matrix {
    const nPeriod: Matrix = {
      start: format(start, "yyyy-MM-dd"),
      end: format(end, "yyyy-MM-dd"),
      rates: [],
    };

    for (let i = 0; i < this.state.data.length; i++) {
      nPeriod.rates.push({
        rateBand: i + 1,
        sun: 999,
        mon: 999,
        tue: 999,
        wed: 999,
        thu: 999,
        fri: 999,
        sat: 999,
      });
    }
    return nPeriod;
  }

  splitPeriod = () => {
    const ms = [...this.state.matrixes];
    const m = ms[this.state.currentIndex];
    const n = this.getNewPeriod(this.state.splitDate, new Date(m.end));
    ms.push(n);
    m.end = format(this.state.splitDate, "yyyy-MM-dd");
    ms.sort((a, b) => differenceInDays(new Date(a.start), new Date(b.start)));

    this.setState({
      currentIndex: this.state.currentIndex + 1,
      matrixes: ms,
      displaySplit: false,
      data: n.rates,
    });
  };

  changePeriod = (event: any) => {
    const index = Number(event.target.value);
    this.setState({
      data: this.state.matrixes[index].rates,
      splitDate: this.computeDefaultSplitdate(index),
      currentIndex: index,
    });
  };

  toggleSplitControl = () => {
    this.setState((prev) => ({ displaySplit: !prev.displaySplit }));
  };

  toggleNewPeriodControl = () => {
    this.setState((prev) => ({ displayNew: !prev.displayNew }));
  };

  preSelectIndex(): number {
    const todayDate = format(new Date(), "yyyy-MM-dd");
    for (let i = 0; i < this.state.matrixes.length; i++) {
      const matrix = this.state.matrixes[i];
      const dateEnd = new Date(matrix.end);
      const dateStart = new Date(matrix.start);
      if (
        isAfter(dateEnd, new Date(todayDate)) &&
        isBefore(dateStart, new Date(todayDate))
      ) {
        return i;
      }
    }
    return this.state.matrixes.length - 1;
  }

  formatData(): Matrix[] {
    const res: Matrix[] = [];
    let i = 0;
    const date1YearAgo = format(subYears(new Date(), 1), "yyyy-MM-dd");

    for (const matrix of this.state.hotel.suggestionParams.matrixes) {
      const dateEnd = new Date(matrix.end);
      if (isAfter(dateEnd, new Date(date1YearAgo))) {
        res[i] = {
          start: matrix.start,
          end: matrix.end,
          rates: [],
        };
        matrix.bands.forEach((band: Band, index: number) => {
          const bandsValues = JSON.parse(band.formula.replace("[dow]", ""));
          res[i].rates.push({
            rateBand: index + 1,
            sun: bandsValues[0],
            mon: bandsValues[1],
            tue: bandsValues[2],
            wed: bandsValues[3],
            thu: bandsValues[4],
            fri: bandsValues[5],
            sat: bandsValues[6],
          });
        });
        i++;
      }
    }
    return res;
  }

  handleAdd = () => {
    const newRow: MatrixRow = {
      rateBand: this.state.data.length + 1,
      sun: 999,
      mon: 999,
      tue: 999,
      wed: 999,
      thu: 999,
      fri: 999,
      sat: 999,
      isEditing: true,
    };
    this.setState((prev) => ({
      data: [...prev.data, newRow],
    }));
  };

  handleEdit = (index: number) => {
    const newData = [...this.state.data];
    newData[index] = { ...newData[index], isEditing: true };
    this.setState({ data: newData });
  };

  handleSave = (index: number) => {
    const row = this.state.data[index];
    const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;

    // Validate all days
    for (const day of days) {
      if (row[day] < 0) {
        alert(
          `${day.charAt(0).toUpperCase() + day.slice(1)}: Negative number is not allowed`,
        );
        return;
      }
    }

    const newData = [...this.state.data];
    newData[index] = { ...row, isEditing: false };
    this.setState({ data: newData });
  };

  handleCancel = (index: number) => {
    const newData = [...this.state.data];
    if (newData[index].rateBand === this.state.data.length) {
      // If it's a new row, remove it
      newData.splice(index, 1);
    } else {
      // Otherwise just cancel editing
      newData[index] = { ...newData[index], isEditing: false };
    }
    this.setState({ data: newData });
  };

  handleDelete = (index: number) => {
    const newData = [...this.state.data];
    newData.splice(index, 1);
    // Update rateBand numbers
    newData.forEach((row, i) => {
      row.rateBand = i + 1;
    });
    this.setState({ data: newData });
  };

  handleChange = (index: number, field: keyof MatrixRow, value: string) => {
    const newData = [...this.state.data];
    newData[index] = {
      ...newData[index],
      [field]: field === "rateBand" ? value : Number(value),
    };
    this.setState({ data: newData });
  };

  render() {
    return (
      <Fragment>
        <ThemeProvider theme={tableTheme}>
          <Grid container spacing={3}>
            {!this.state.displayNew && (
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <FormControl>
                    <InputLabel>Period</InputLabel>
                    <Select
                      style={{ width: "100%" }}
                      value={this.state.currentIndex.toString()}
                      onChange={this.changePeriod}
                    >
                      {this.state.matrixes.map((matrix, index) => (
                        <MenuItem value={index} key={index}>
                          {format(new Date(matrix.start), "yyyy-MM-dd")} -{" "}
                          {format(new Date(matrix.end), "yyyy-MM-dd")}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            )}
            {!this.state.displaySplit && !this.state.displayNew && (
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Button
                    color="info"
                    variant="contained"
                    startIcon={<AddOutlined />}
                    onClick={this.toggleSplitControl}
                    id="split-period"
                  >
                    Split Period
                  </Button>
                </Box>
              </Grid>
            )}
            {this.state.displaySplit && (
              <div>
                <TextField
                  defaultValue={
                    this.state.matrixes[this.state.currentIndex].start
                  }
                  style={{ width: "50%", marginLeft: "50px" }}
                  variant="outlined"
                  label="Split Date"
                  type="date"
                  onChange={(e) => this.setSplitDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: this.state.matrixes[this.state.currentIndex].start,
                    max: this.state.matrixes[this.state.currentIndex].end,
                  }}
                />
                <IconButton onClick={this.splitPeriod}>
                  <Check htmlColor="#4caf50" />
                </IconButton>
                <IconButton onClick={this.toggleSplitControl} color="secondary">
                  <Clear />
                </IconButton>
              </div>
            )}
            {!this.state.displaySplit && !this.state.displayNew && (
              <Grid item xs={12} sm={6} md={4}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Button
                    color="info"
                    variant="contained"
                    startIcon={<AddOutlined />}
                    onClick={this.toggleNewPeriodControl}
                    id="new-period"
                  >
                    New Period
                  </Button>
                </Box>
              </Grid>
            )}
            {this.state.displayNew && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <TextField
                  value={format(this.state.newStartDate, "yyyy-MM-dd")}
                  style={{ width: "50%", marginLeft: "50px" }}
                  variant="outlined"
                  label="Start"
                  type="date"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: format(this.state.newStartDate, "yyyy-MM-dd"),
                    readOnly: true,
                  }}
                />
                <TextField
                  value={
                    this.state.newEndDate
                      ? format(this.state.newEndDate, "yyyy-MM-dd")
                      : ""
                  }
                  style={{ width: "50%", marginLeft: "50px" }}
                  variant="outlined"
                  label="End"
                  type="date"
                  onChange={(e) => this.setEndDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    min: format(this.state.newStartDate, "yyyy-MM-dd"),
                  }}
                />
                <IconButton
                  onClick={this.createNewPeriod}
                  color="primary"
                  disabled={!this.state.newEndDate}
                >
                  <Check htmlColor="#4caf50" />
                </IconButton>
                <IconButton
                  onClick={this.toggleNewPeriodControl}
                  color="secondary"
                >
                  <Clear />
                </IconButton>
              </Box>
            )}
          </Grid>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Rate band</TableCell>
                  <TableCell>Mon</TableCell>
                  <TableCell>Tue</TableCell>
                  <TableCell>Wed</TableCell>
                  <TableCell>Thu</TableCell>
                  <TableCell>Fri</TableCell>
                  <TableCell>Sat</TableCell>
                  <TableCell>Sun</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {this.state.data.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.rateBand}</TableCell>
                    {(
                      ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const
                    ).map((day) => (
                      <TableCell key={day}>
                        {row.isEditing ? (
                          <TextField
                            type="number"
                            value={row[day]}
                            onChange={(e) =>
                              this.handleChange(index, day, e.target.value)
                            }
                            size="small"
                            fullWidth
                          />
                        ) : (
                          row[day]
                        )}
                      </TableCell>
                    ))}
                    <TableCell>
                      {row.isEditing ? (
                        <>
                          <IconButton
                            onClick={() => this.handleSave(index)}
                            size="small"
                            color="primary"
                          >
                            <Check />
                          </IconButton>
                          <IconButton
                            onClick={() => this.handleCancel(index)}
                            size="small"
                            color="error"
                          >
                            <Clear />
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <IconButton
                            onClick={() => this.handleEdit(index)}
                            size="small"
                            color="primary"
                          >
                            <EditOutlined />
                          </IconButton>
                          <IconButton
                            onClick={() => this.handleDelete(index)}
                            size="small"
                            color="error"
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <IconButton onClick={this.handleAdd} color="primary">
              <AddOutlined />
            </IconButton>
          </Box>
        </ThemeProvider>
      </Fragment>
    );
  }
}
